import { useMemo, useCallback } from 'react'
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Progress } from '@/components/ui/progress'
import { ColorDot } from '@/components/ui/color-dot'
import { TransactionListItem } from '@/components/transaction/transaction-list-item'
import { formatAmount, cn } from '@/lib/utils'
import type { BudgetStatusItem } from '@/lib/api/budgets'
import type { Transaction } from '@/lib/api/transactions'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  budgetItem: BudgetStatusItem | null
  transactions: Transaction[]
  elapsedDays: number
  totalDays: number
  subcategoryBudgetItems?: BudgetStatusItem[]
}

type CategoryTransaction = {
  tx: Transaction
  categoryAmount: number
}

export function BudgetDetailSheet({
  open,
  onOpenChange,
  budgetItem,
  transactions,
  elapsedDays,
  totalDays,
  subcategoryBudgetItems,
}: Props) {
  const categoryTransactions = useMemo<CategoryTransaction[]>(() => {
    if (!budgetItem) return []
    const entries: CategoryTransaction[] = []
    for (const tx of transactions) {
      let amount = 0
      for (const item of tx.items) {
        const matchName = item.parent_category_name ?? item.category_name
        if (matchName === budgetItem.category_name) {
          amount += item.amount
        }
      }
      if (amount > 0) {
        entries.push({ tx, categoryAmount: amount })
      }
    }
    return entries.sort((a, b) => b.tx.date.localeCompare(a.tx.date))
  }, [transactions, budgetItem])

  const subcategories = useMemo(() => {
    if (!budgetItem) return []
    const map = new Map<string, number>()
    for (const tx of transactions) {
      for (const item of tx.items) {
        const matchName = item.parent_category_name ?? item.category_name
        if (matchName === budgetItem.category_name && item.parent_category_name) {
          const subName = item.category_name ?? budgetItem.category_name
          map.set(subName, (map.get(subName) ?? 0) + item.amount)
        }
      }
    }
    const total = Array.from(map.values()).reduce((s, v) => s + v, 0)
    return Array.from(map.entries())
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
  }, [transactions, budgetItem])

  // Burndown chart data — must be before early return to keep hooks order stable
  const burndownData = useMemo(() => {
    if (!budgetItem || totalDays <= 0) return []
    const budget = budgetItem.budget_amount
    if (budget <= 0) return []

    // When subcategory budgets exist, only count spending from budgeted subcategories
    const budgetedSubNames = subcategoryBudgetItems && subcategoryBudgetItems.some(s => s.budget_amount > 0)
      ? new Set(subcategoryBudgetItems.filter(s => s.budget_amount > 0).map(s => s.category_name))
      : null

    // Daily spending map (day number → amount)
    const dailyMap = new Map<number, number>()
    for (const { tx } of categoryTransactions) {
      const day = parseInt(tx.date.split('-')[2], 10)
      let amount = 0
      for (const item of tx.items) {
        const matchName = item.parent_category_name ?? item.category_name
        if (matchName !== budgetItem.category_name) continue
        // If subcategory budgets exist, skip unbudgeted subcategories
        if (budgetedSubNames && item.parent_category_name && !budgetedSubNames.has(item.category_name ?? '')) continue
        amount += item.amount
      }
      if (amount > 0) {
        dailyMap.set(day, (dailyMap.get(day) ?? 0) + amount)
      }
    }

    const data: { day: number; ideal: number; actual: number | null }[] = []
    let cumulative = 0

    for (let d = 1; d <= totalDays; d++) {
      const ideal = Math.round(budget - (d / totalDays) * budget)
      if (d <= elapsedDays) {
        cumulative += dailyMap.get(d) ?? 0
        data.push({ day: d, ideal, actual: budget - cumulative })
      } else {
        data.push({ day: d, ideal, actual: null })
      }
    }

    return data
  }, [budgetItem, categoryTransactions, subcategoryBudgetItems, totalDays, elapsedDays])

  // Get transactions for a specific subcategory
  const getSubcategoryTransactions = useCallback((subCategoryName: string) => {
    if (!budgetItem) return []
    const entries: CategoryTransaction[] = []
    for (const tx of transactions) {
      let amount = 0
      for (const item of tx.items) {
        if (item.parent_category_name === budgetItem.category_name && item.category_name === subCategoryName) {
          amount += item.amount
        }
      }
      if (amount > 0) {
        entries.push({ tx, categoryAmount: amount })
      }
    }
    return entries.sort((a, b) => b.tx.date.localeCompare(a.tx.date))
  }, [transactions, budgetItem])

  if (!budgetItem) return null

  const displayBudget = budgetItem.budget_amount
  const displaySpent = budgetItem.spent_amount
  const displayRemaining = displayBudget - displaySpent
  const ratio = displayBudget > 0 ? (displaySpent / displayBudget) * 100 : 0
  const progressValue = Math.min(ratio, 100)
  const isOver = displayRemaining < 0

  // Pace diff: ideal spending vs actual at this point
  const idealSpent = totalDays > 0
    ? Math.round((elapsedDays / totalDays) * displayBudget)
    : 0
  const paceDiff = idealSpent - displaySpent

  // Stacked bar segments: each subcategory's spending as % of budget
  // When subcategory budgets exist, only show budgeted subcategories in the bar
  const budgetedSubNames = subcategoryBudgetItems && subcategoryBudgetItems.some(s => s.budget_amount > 0)
    ? new Set(subcategoryBudgetItems.filter(s => s.budget_amount > 0).map(s => s.category_name))
    : null
  const barSubcategories = budgetedSubNames
    ? subcategories.filter(sub => budgetedSubNames.has(sub.name))
    : subcategories
  const stackedSegments = barSubcategories.length > 0 && displayBudget > 0
    ? barSubcategories.map((sub, i) => ({
        name: sub.name,
        widthPercent: Math.min((sub.amount / displayBudget) * 100, 100),
        budgetPercent: Math.round((sub.amount / displayBudget) * 100),
        opacity: 1 - i * 0.15,
      }))
    : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[85vh] overflow-y-auto focus:outline-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>
            <span className="flex items-center gap-2">
              {budgetItem.color && <ColorDot color={budgetItem.color} size="md" />}
              {budgetItem.category_name}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {budgetItem.category_name}の予算詳細
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 px-4 pb-8">
          {/* ── 予算セクション ── */}
          <section className="rounded-xl bg-muted/40 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground tracking-wide">予算</h3>
            <div className="flex items-baseline gap-2">
              {!isOver && (
                <span className="text-sm text-muted-foreground">あと</span>
              )}
              <span
                className={cn(
                  'text-3xl font-bold tabular-nums',
                  isOver && 'text-destructive',
                )}
              >
                {formatAmount(Math.abs(displayRemaining))}
              </span>
              {isOver && (
                <span className="text-sm text-destructive">超過</span>
              )}
            </div>
            {stackedSegments ? (
              <div className="space-y-1.5">
                <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-primary/20">
                  {stackedSegments.map((seg, i) => {
                    const color = budgetItem.color ?? 'currentColor'
                    const nextOpacity = stackedSegments[i + 1]?.opacity ?? seg.opacity * 0.7
                    return (
                      <div
                        key={seg.name}
                        className="h-full first:rounded-l-full last:rounded-r-full"
                        style={{
                          width: `${seg.widthPercent}%`,
                          background: `linear-gradient(to right, color-mix(in srgb, ${color} ${Math.round(seg.opacity * 100)}%, transparent), color-mix(in srgb, ${color} ${Math.round(nextOpacity * 100)}%, transparent))`,
                          minWidth: seg.widthPercent > 0 ? '2px' : undefined,
                        }}
                      />
                    )
                  })}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                  {stackedSegments.map((seg) => (
                    <span key={seg.name} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span
                        className="inline-block size-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: budgetItem.color ?? undefined, opacity: seg.opacity }}
                      />
                      {seg.name} {seg.budgetPercent}%
                    </span>
                  ))}
                  {Math.round(ratio) < 100 && (
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="inline-block size-1.5 shrink-0 rounded-full border border-muted-foreground/40" />
                      残り {100 - Math.round(ratio)}%
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <Progress value={progressValue} />
            )}
            <p className="text-xs text-muted-foreground tabular-nums">
              {formatAmount(displaySpent)} / {formatAmount(displayBudget)}
            </p>

            {/* Burndown chart */}
            {burndownData.length > 0 && (
              <div className="space-y-1.5 border-t pt-3">
                <h4 className="text-xs text-muted-foreground">
                  ペース（{elapsedDays}/{totalDays}日経過）
                </h4>
                <div className="h-28" data-testid="burndown-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={burndownData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                      <defs>
                        <linearGradient id={`burndown-fill-${budgetItem.category_id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={budgetItem.color ?? 'currentColor'} stopOpacity={0.25} />
                          <stop offset="100%" stopColor={budgetItem.color ?? 'currentColor'} stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 10 }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis hide domain={[0, displayBudget]} />
                      <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.1} />
                      {/* Ideal pace — dashed line */}
                      <Line
                        dataKey="ideal"
                        stroke="currentColor"
                        strokeOpacity={0.2}
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                      {/* Actual remaining — filled area */}
                      <Area
                        dataKey="actual"
                        stroke={budgetItem.color ?? 'currentColor'}
                        strokeWidth={2}
                        fill={`url(#burndown-fill-${budgetItem.category_id})`}
                        connectNulls={false}
                        isAnimationActive={false}
                        dot={(props: Record<string, unknown>) => {
                          const { cx, cy, payload, height: chartH, offset: chartOffset } = props as { cx: number; cy: number; payload: { day: number }; height?: number; offset?: number }
                          if (payload.day !== elapsedDays) return <circle key={payload.day} r={0} />
                          // Place label above dot, but flip below if too close to top
                          const topEdge = chartOffset ?? 4
                          const labelAbove = cy - 14
                          const labelBelow = cy + 18
                          const labelY = labelAbove > topEdge ? labelAbove : labelBelow
                          return (
                            <g key={payload.day}>
                              <circle cx={cx} cy={cy} r={3} fill={budgetItem.color ?? 'currentColor'} />
                              <text
                                x={cx}
                                y={labelY}
                                textAnchor="middle"
                                fontSize={10}
                                fontWeight={600}
                                fill="currentColor"
                              >
                                {formatAmount(displaySpent)}
                              </text>
                            </g>
                          )
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 border-t-2 border-dashed border-current opacity-30" />
                      理想
                    </span>
                    <span className="flex items-center gap-1">
                      <span
                        className="inline-block w-3 border-t-2"
                        style={{ borderColor: budgetItem.color ?? 'currentColor' }}
                      />
                      実績
                    </span>
                  </div>
                  {paceDiff !== 0 && (
                    <span className={cn(
                      'text-[10px] font-medium',
                      paceDiff > 0 ? 'text-emerald-600' : 'text-destructive',
                    )}>
                      理想の消費ペースより{paceDiff > 0 ? '+' : '-'}{formatAmount(Math.abs(paceDiff))}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Per-category breakdown — always shown */}
            {subcategories.length > 0 && (
              <div className="space-y-2 border-t pt-3">
                <h4 className="text-xs text-muted-foreground">カテゴリ別</h4>
                <Accordion type="single" collapsible>
                  {(() => {
                    // Build a merged list: subcategory budget items + spending-only subcategories
                    const hasSubBudgets = subcategoryBudgetItems && subcategoryBudgetItems.length > 0
                    const budgetMap = new Map(
                      (subcategoryBudgetItems ?? []).map(s => [s.category_name, s])
                    )

                    // Sort: budgeted first, then unbudgeted, each group by spending desc
                    const sorted = [...subcategories].sort((a, b) => {
                      const aB = budgetMap.get(a.name)?.budget_amount ?? 0
                      const bB = budgetMap.get(b.name)?.budget_amount ?? 0
                      if ((aB > 0) !== (bB > 0)) return aB > 0 ? -1 : 1
                      return b.amount - a.amount
                    })

                    return sorted.map(sub => {
                      const budgetItem_ = budgetMap.get(sub.name)
                      const isBudgeted = budgetItem_ ? budgetItem_.budget_amount > 0 : false
                      const subBudgetAmount = budgetItem_?.budget_amount ?? 0
                      const subRatio = isBudgeted ? (sub.amount / subBudgetAmount) * 100 : 0
                      const subRemaining = subBudgetAmount - sub.amount
                      const subIsOver = isBudgeted && subRemaining < 0
                      const subProgressColor = isBudgeted && budgetItem.color ? budgetItem.color : undefined
                      const subTxs = getSubcategoryTransactions(sub.name)

                      return (
                        <AccordionItem key={sub.name} value={sub.name} className="border-b-0">
                          <AccordionTrigger className="py-1.5 hover:no-underline focus-visible:ring-0 [&>svg]:size-3.5">
                            <div className="flex-1 space-y-0.5 text-left">
                              <div className="flex items-center justify-between text-xs">
                                <span className={cn('flex items-center gap-1', !isBudgeted && !hasSubBudgets && 'text-muted-foreground')}>
                                  {budgetItem.color && <ColorDot color={budgetItem.color} size="sm" />}
                                  {sub.name}
                                </span>
                                {isBudgeted ? (
                                  <span className={cn(
                                    'tabular-nums',
                                    subIsOver ? 'text-destructive' : 'text-muted-foreground',
                                  )}>
                                    {subIsOver
                                      ? `${formatAmount(Math.abs(subRemaining))} 超過`
                                      : `あと ${formatAmount(subRemaining)}`}
                                  </span>
                                ) : hasSubBudgets ? null : (
                                  <span className="text-muted-foreground tabular-nums">
                                    {formatAmount(sub.amount)}
                                  </span>
                                )}
                              </div>
                              {isBudgeted && (
                                <>
                                  <Progress
                                    value={Math.min(subRatio, 100)}
                                    className={cn('h-1.5', subProgressColor && '[&>[data-slot=progress-indicator]]:bg-[var(--sub-color)]')}
                                    style={subProgressColor ? { '--sub-color': subProgressColor } as React.CSSProperties : undefined}
                                  />
                                  <div className="flex justify-end text-[10px] text-muted-foreground tabular-nums">
                                    <span>{formatAmount(sub.amount)} / {formatAmount(subBudgetAmount)}</span>
                                  </div>
                                </>
                              )}
                              {hasSubBudgets && !isBudgeted && (
                                <>
                                  <Progress
                                    value={displayBudget > 0 ? Math.min((sub.amount / displayBudget) * 100, 100) : 0}
                                    className="h-1.5 opacity-20 [&>[data-slot=progress-indicator]]:bg-[var(--sub-color)]"
                                    style={budgetItem.color ? { '--sub-color': budgetItem.color } as React.CSSProperties : undefined}
                                  />
                                  <div className="flex justify-end text-[10px] text-muted-foreground tabular-nums">
                                    <span>{formatAmount(sub.amount)} / 予算未設定</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {subTxs.length > 0 ? (
                              <div className="space-y-1 pt-1">
                                {subTxs.map(({ tx, categoryAmount }) => (
                                  <TransactionListItem
                                    key={tx.id}
                                    transaction={tx}
                                    to={`/transactions/${tx.id}?month=${tx.date.substring(0, 7)}`}
                                    overrideAmount={categoryAmount}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="py-2 text-center text-xs text-muted-foreground">取引なし</p>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      )
                    })
                  })()}
                </Accordion>
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}
